import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { graphApi } from '@/lib/api';
import { db } from '@/lib/db';
import { useWorkspaceStore, selectNodesList, selectEdgesList } from '@/stores/workspaceStore';
import { useUIStore } from '@/stores/uiStore';
import type {
  CapabilityNode,
  Edge,
  GraphData,
  GraphNode,
  GraphEdge,
  CreateEdgeInput,
  CapabilityType,
  CapabilityStatus,
} from '@/types';

// ─── Query keys ───────────────────────────────────────────────────────────────

export const graphKeys = {
  all: ['graph'] as const,
  nodes: (workspaceId: string) => [...graphKeys.all, 'nodes', workspaceId] as const,
  edges: (workspaceId: string) => [...graphKeys.all, 'edges', workspaceId] as const,
};

// ─── Fetch graph nodes ─────────────────────────────────────────────────────────

export function useGraphNodes(workspaceId: string | null) {
  const setNodes = useWorkspaceStore((s) => s.setNodes);

  return useQuery({
    queryKey: graphKeys.nodes(workspaceId ?? ''),
    queryFn: async () => {
      if (!workspaceId) {
        return [];
      }
      const nodes = await graphApi.getNodes(workspaceId);
      setNodes(nodes);
      await db.nodes.bulkPut(nodes);
      return nodes;
    },
    enabled: Boolean(workspaceId),
    staleTime: 60_000,
  });
}

// ─── Fetch graph edges ─────────────────────────────────────────────────────────

export function useGraphEdges(workspaceId: string | null) {
  const setEdges = useWorkspaceStore((s) => s.setEdges);

  return useQuery({
    queryKey: graphKeys.edges(workspaceId ?? ''),
    queryFn: async () => {
      if (!workspaceId) {
        return [];
      }
      const edges = await graphApi.getEdges(workspaceId);
      setEdges(edges);
      await db.edges.bulkPut(edges);
      return edges;
    },
    enabled: Boolean(workspaceId),
    staleTime: 60_000,
  });
}

// ─── Build graph data for Cytoscape ───────────────────────────────────────────

export function useGraphData(workspaceId: string | null): {
  graphData: GraphData;
  isLoading: boolean;
} {
  const nodes = useWorkspaceStore(selectNodesList);
  const edges = useWorkspaceStore(selectEdgesList);
  const capabilities = useWorkspaceStore((s) => s.capabilities);

  const { isLoading: nodesLoading } = useGraphNodes(workspaceId);
  const { isLoading: edgesLoading } = useGraphEdges(workspaceId);

  const graphNodes: GraphNode[] = nodes
    .map((node): GraphNode | null => {
      const cap = capabilities[node.capabilityId];
      if (!cap) {
        return null;
      }
      return {
        id: node.id,
        data: {
          label: cap.title,
          type: cap.type,
          status: cap.status,
          strength: cap.strength,
          capabilityId: cap.id,
          tags: cap.tags,
        },
        position: node.position,
      };
    })
    .filter((n): n is GraphNode => n !== null);

  const graphEdges: GraphEdge[] = edges.map((edge): GraphEdge => ({
    id: edge.id,
    source: edge.sourceNodeId,
    target: edge.targetNodeId,
    data: {
      type: edge.type,
      label: edge.label,
      weight: edge.weight,
      bidirectional: edge.bidirectional,
    },
  }));

  return {
    graphData: { nodes: graphNodes, edges: graphEdges },
    isLoading: nodesLoading || edgesLoading,
  };
}

// ─── Create edge ───────────────────────────────────────────────────────────────

export function useCreateEdge(workspaceId: string) {
  const queryClient = useQueryClient();
  const upsertEdge = useWorkspaceStore((s) => s.upsertEdge);
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: (input: CreateEdgeInput) => graphApi.createEdge(workspaceId, input),
    onSuccess: async (edge) => {
      upsertEdge(edge);
      await db.edges.put(edge);
      await queryClient.invalidateQueries({ queryKey: graphKeys.edges(workspaceId) });
      addToast({ title: 'Connection created', variant: 'success' });
    },
    onError: (error: Error) => {
      addToast({ title: 'Failed to create connection', description: error.message, variant: 'error' });
    },
  });
}

// ─── Delete edge ───────────────────────────────────────────────────────────────

export function useDeleteEdge(workspaceId: string) {
  const queryClient = useQueryClient();
  const removeEdge = useWorkspaceStore((s) => s.removeEdge);
  const edges = useWorkspaceStore(selectEdgesList);
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: (edgeId: string) => graphApi.deleteEdge(workspaceId, edgeId),
    onMutate: async (edgeId) => {
      const previous = edges.find((e) => e.id === edgeId);
      removeEdge(edgeId);
      return { previous };
    },
    onSuccess: async (_, edgeId) => {
      await db.edges.delete(edgeId);
      await queryClient.invalidateQueries({ queryKey: graphKeys.edges(workspaceId) });
    },
    onError: (error: Error, _id, context) => {
      if (context?.previous) {
        upsertEdge(context.previous);
      }
      addToast({ title: 'Failed to delete connection', description: error.message, variant: 'error' });
    },
  });
}

// Helper
function upsertEdge(edge: Edge) {
  useWorkspaceStore.getState().upsertEdge(edge);
}

// ─── Update node position ──────────────────────────────────────────────────────

export function useUpdateNodePosition(workspaceId: string) {
  const upsertNode = useWorkspaceStore((s) => s.upsertNode);
  const nodes = useWorkspaceStore((s) => s.nodes);

  return useMutation({
    mutationFn: ({ nodeId, position }: { nodeId: string; position: { x: number; y: number } }) =>
      graphApi.updateNodePosition(workspaceId, nodeId, position),
    onMutate: ({ nodeId, position }) => {
      const existing = nodes[nodeId];
      if (existing) {
        upsertNode({ ...existing, position });
      }
    },
    onSuccess: (node) => {
      upsertNode(node);
    },
  });
}

// ─── Graph traversal helpers ──────────────────────────────────────────────────

export function useNeighbors(nodeId: string | null) {
  const edges = useWorkspaceStore(selectEdgesList);
  const nodes = useWorkspaceStore((s) => s.nodes);

  if (!nodeId) {
    return { incoming: [] as CapabilityNode[], outgoing: [] as CapabilityNode[] };
  }

  const outgoingEdges = edges.filter((e) => e.sourceNodeId === nodeId);
  const incomingEdges = edges.filter((e) => e.targetNodeId === nodeId);

  const outgoing = outgoingEdges
    .map((e) => nodes[e.targetNodeId])
    .filter((n): n is CapabilityNode => Boolean(n));

  const incoming = incomingEdges
    .map((e) => nodes[e.sourceNodeId])
    .filter((n): n is CapabilityNode => Boolean(n));

  return { incoming, outgoing };
}

// ─── Filter graph data ────────────────────────────────────────────────────────

export function useFilteredGraphData(
  graphData: GraphData,
  filterTypes: string[],
  filterStatuses: string[]
): GraphData {
  const filteredNodes =
    filterTypes.length === 0 && filterStatuses.length === 0
      ? graphData.nodes
      : graphData.nodes.filter((node) => {
          const typeMatch =
            filterTypes.length === 0 ||
            filterTypes.includes(node.data.type as CapabilityType);
          const statusMatch =
            filterStatuses.length === 0 ||
            filterStatuses.includes(node.data.status as CapabilityStatus);
          return typeMatch && statusMatch;
        });

  const nodeIds = new Set(filteredNodes.map((n) => n.id));
  const filteredEdges = graphData.edges.filter(
    (e) => nodeIds.has(e.source) && nodeIds.has(e.target)
  );

  return { nodes: filteredNodes, edges: filteredEdges };
}
